import { EventEntity } from '../../apis/event/event-entity';
import { EventVisibility } from '../../apis/event/event-visibility';
import { Sport, SPORTS, sportLocaleKey } from '../../apis/event/sport';
import { inject } from 'aurelia';
import { IRouter } from '@aurelia/router';
import { I18N } from '@aurelia/i18n';
import { DatePart } from '../../infrastructure/date-part';
import { ProjectModelService } from '../../services/project-model-service';

@inject(ProjectModelService, IRouter, I18N)
export class EditEvent {
    // Exposed so the view can bind <option> values to EventVisibility.Public / .Private.
    public readonly EventVisibility = EventVisibility;
    private _eventEntity: EventEntity;
    private _projectModelService: ProjectModelService;
    private _router: IRouter;
    private _i18n: I18N;

    constructor(projectModelService: ProjectModelService, router: IRouter, i18n: I18N) {
        this._projectModelService = projectModelService;
        this._router = router;
        this._i18n = i18n;
    }

    public async loading(params: any): Promise<void> {
        const project = await this._projectModelService.loadByEventId(params.eventId, false);
        this._eventEntity = project.event;
    }

    public get isNew(): boolean {
        return this._eventEntity?.code == null;
    }

    public get title(): string {
        return this._eventEntity?.title;
    }
    public set title(value: string) {
        this._eventEntity.title = value;
    }
    public get organizer(): string {
        return this._eventEntity?.organizer;
    }
    public set organizer(value: string) {
        this._eventEntity.organizer = value;
    }

    public get startDate(): string {
        return this._eventEntity?.startDate.toString();
    }
    public set startDate(value: string) {
        if (this._eventEntity == null) {
            return;
        }

        const startDate = new DatePart(value);
        this._eventEntity.startDate = startDate;

        if (this._eventEntity.endDate == null || this._eventEntity.endDate.isBefore(startDate)) {
            this._eventEntity.endDate = startDate;
        }
    }

    public get endDate(): string {
        return this._eventEntity?.endDate.toString();
    }
    public set endDate(value: string) {
        this._eventEntity.endDate = new DatePart(value);
    }
    public get registrationEnabled(): boolean {
        return this._eventEntity?.registrationEnabled ?? false;
    }
    public set registrationEnabled(value: boolean) {
        if (this._eventEntity == null) {
            return;
        }
        this._eventEntity.registrationEnabled = value;
        if (!value && this._eventEntity.registrationExpirationDate != null) {
            this._eventEntity.registrationExpirationDate = null;
        } else if (value && this._eventEntity.registrationExpirationDate == null) {
            this._eventEntity.registrationExpirationDate = DatePart.now().add(7, 'days');
        }
    }
    public get showRegistrationExpirationDate(): boolean {
        return this.registrationEnabled;
    }
    public get registrationExpirationDate(): string {
        return this._eventEntity?.registrationExpirationDate?.toString() ?? '';
    }
    public set registrationExpirationDate(value: string) {
        if (this._eventEntity == null) {
            return;
        }
        this._eventEntity.registrationExpirationDate = value ? new DatePart(value) : null;
    }
    public get description(): string {
        return this._eventEntity?.description;
    }
    public set description(value: string) {
        this._eventEntity.description = value;
    }

    public get location(): string {
        return this._eventEntity?.location;
    }
    public set location(value: string) {
        this._eventEntity.location = value;
    }

    public get visibility(): EventVisibility {
        return this._eventEntity?.visibility ?? EventVisibility.Public;
    }
    public set visibility(value: EventVisibility) {
        if (this._eventEntity == null) {
            return;
        }
        this._eventEntity.visibility = value;
    }

    public get latitude(): number | null {
        return this._eventEntity?.latitude ?? null;
    }
    public set latitude(value: number | null) {
        this._eventEntity.latitude = value;
    }

    public get longitude(): number | null {
        return this._eventEntity?.longitude ?? null;
    }
    public set longitude(value: number | null) {
        this._eventEntity.longitude = value;
    }

    public get sport(): Sport {
        return this._eventEntity?.sport ?? Sport.NotSet;
    }
    public set sport(value: Sport) {
        if (this._eventEntity == null) {
            return;
        }
        this._eventEntity.sport = value;
        if (value !== Sport.Other) {
            this._eventEntity.sportOther = '';
        }
    }

    public get showSportOther(): boolean {
        return this.sport === Sport.Other;
    }

    public get sportOther(): string {
        return this._eventEntity?.sportOther ?? '';
    }
    public set sportOther(value: string) {
        this._eventEntity.sportOther = value;
    }

    public get sportOptions(): { value: Sport; label: string }[] {
        return SPORTS.map(value => ({ value, label: this._i18n.tr(`ui:${sportLocaleKey(value)}`) }));
    }

    public ok(): void {
        void this._router.load(`create/event-summary/${this._eventEntity.id}`);
    }

    public cancel(): void {
        void this._router.load(`create/event-summary/${this._eventEntity.id}`);
    }
}
